/**
 * SettoriMerceologiciSelect — v1.72.1 (feb-2026).
 *
 * Dropdown a 2 livelli macro-categoria → settore specifico per Polizza
 * Commercio (Tariffa Mod. 3067 INC Ed. 03/2026, 84 settori censiti).
 *
 * Fetch al mount da GET /api/commercio/settori (endpoint pubblico, campi
 * interni rating/routing/note già strip-pati lato backend).
 *
 * Regime speciale: mostra badge "Valutazione manuale" (non blocca il submit
 * in Fase B — Sub-task 5 aggiungerà le soft-block).
 */
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * @param {object} props
 * @param {{macro?: string, settore?: string}} props.value
 * @param {(next:{macro:string, settore:string}) => void} props.onChange
 * @param {string}  [props.testidPrefix="settori"]
 * @param {boolean} [props.disabled]
 */
export default function SettoriMerceologiciSelect({
  value = {},
  onChange,
  testidPrefix = "settori",
  disabled = false,
}) {
  const [state, setState] = useState({ loading: true, error: null, macro: [], settori: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/commercio/settori`, { credentials: "omit" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setState({
            loading: false,
            error: null,
            macro: data.macro_categorie || [],
            settori: data.settori || [],
          });
        }
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: String(err?.message || err), macro: [], settori: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const settoriByMacro = useMemo(() => {
    const map = {};
    for (const s of state.settori) {
      (map[s.macro_categoria] = map[s.macro_categoria] || []).push(s);
    }
    // Ordina i settori per nome all'interno di ciascuna macro
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.nome.localeCompare(b.nome, "it")));
    return map;
  }, [state.settori]);

  const selectedSettore = state.settori.find((s) => s.codice === value.settore);
  const isRegimeSpeciale = value.macro === "regime_speciale";

  // ── Loading skeleton
  if (state.loading) {
    return (
      <div className="grid sm:grid-cols-2 gap-3" data-testid={`${testidPrefix}-loading`}>
        <div className="h-10 rounded-xl bg-[#F5F4F0] animate-pulse flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-[#4A5E51]" />
        </div>
        <div className="h-10 rounded-xl bg-[#F5F4F0] animate-pulse" />
      </div>
    );
  }

  // ── Error fallback (textarea libera)
  if (state.error) {
    return (
      <div className="space-y-2" data-testid={`${testidPrefix}-error`}>
        <p className="text-xs text-[#D97B58] flex items-start gap-1">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          Impossibile caricare l'elenco settori — verrai contattato per classificazione manuale.
        </p>
        <label className="text-[11px] uppercase tracking-wide text-[#4A5E51]">Descrivi la tua attività *</label>
        <textarea
          className="w-full mt-1 p-3 border border-[#E5E2DB] rounded-xl text-sm min-h-[60px]"
          maxLength={500}
          placeholder="Es. Vendo capi di abbigliamento sportivo e articoli tecnici caccia/pesca..."
          onChange={(e) => onChange?.({ macro: "", settore: "", descrizione_libera: e.target.value })}
          data-testid={`${testidPrefix}-descrizione-libera`}
        />
      </div>
    );
  }

  const setMacro = (m) => onChange?.({ macro: m, settore: "" });
  const setSettore = (s) => {
    const meta = state.settori.find((x) => x.codice === s);
    onChange?.({ macro: meta?.macro_categoria || value.macro || "", settore: s });
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`${testidPrefix}-block`}>
      <div>
        <label className="text-[11px] uppercase tracking-wide text-[#4A5E51]">Macro-categoria *</label>
        <select
          value={value.macro || ""}
          onChange={(e) => setMacro(e.target.value)}
          disabled={disabled}
          className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm bg-white"
          data-testid={`${testidPrefix}-macro`}
        >
          <option value="">Seleziona macro-categoria…</option>
          {state.macro.map((m) => (
            <option key={m.codice} value={m.codice}>{m.nome}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-wide text-[#4A5E51]">Settore specifico *</label>
        <select
          value={value.settore || ""}
          onChange={(e) => setSettore(e.target.value)}
          disabled={disabled || !value.macro}
          className="w-full mt-1 h-10 px-3 border border-[#E5E2DB] rounded-xl text-sm bg-white disabled:opacity-60"
          data-testid={`${testidPrefix}-settore`}
        >
          <option value="">
            {value.macro ? "Seleziona settore…" : "Scegli prima una macro-categoria"}
          </option>
          {(settoriByMacro[value.macro] || []).map((s) => (
            <option key={s.codice} value={s.codice}>{s.nome}</option>
          ))}
        </select>
      </div>

      {/* Badge regime speciale (visual only in Fase B, no submit block) */}
      {isRegimeSpeciale && value.settore && (
        <div
          className="sm:col-span-2 flex items-start gap-2 px-3 py-2.5 rounded-xl border border-[#C9A96E]/40 bg-[#F5EFDD]"
          data-testid={`${testidPrefix}-badge-regime-speciale`}
        >
          <AlertTriangle className="w-4 h-4 text-[#8B6F2C] mt-0.5 flex-shrink-0" />
          <span className="text-[12px] text-[#5C4A1F] leading-snug">
            <strong>Valutazione manuale.</strong> Il settore selezionato&nbsp;
            <em>{selectedSettore?.nome}</em>&nbsp;richiede una quotazione dedicata:
            un consulente ti contatterà per completare l'analisi del rischio.
          </span>
        </div>
      )}
    </div>
  );
}
